<template>
  <Modal :is_show="showModal" class="new-chat-modal" @click-bg="close">
    <Loading class="static" v-if="isLoading" />

    <div class="box" v-else>
      <header>
        <h2>New Chat</h2>
      </header>
      <div class="form-item">
        <label>To</label>
        <el-select
          ref="selectRef"
          v-model="value"
          multiple
          filterable
          default-first-option
          :reserve-keyword="false"
          placeholder="Search creator name or tag"
          :remote="true"
          :remote-method="remoteSearch"
          :loading="isSearching"
          @visible-change="handleVisibleChange"
        >
          <el-option v-for="item in filteredUsers" :key="item.id" :label="item.name" :value="item.id" />
          <el-option v-if="loadingMore" disabled key="loading-more" value="loading-more" class="loading-more-option">
            <div class="select-loading-more">
              <svg class="circular" viewBox="0 0 50 50">
                <circle class="path" cx="25" cy="25" r="20" fill="none"></circle>
              </svg>
              <span>Loading more...</span>
            </div>
          </el-option>
        </el-select>
        <p>* Messages sent to more than one creator will be sent as individual chats.</p>
      </div>

      <div class="form-item">
        <label>Message:</label>
        <el-input
          v-model="message"
          maxlength="500"
          placeholder="New message"
          show-word-limit
          rows="6"
          type="textarea"
        />
      </div>

      <p v-if="isShowError1" class="error-msg">Please select at least one creator.</p>
      <p v-if="isShowError2" class="error-msg">Cannot send empty chats.</p>

      <footer>
        <el-button @click="close">Cancel</el-button>
        <el-button type="primary" @click="send" border_r="6" :loading="isSending">Send</el-button>
      </footer>
    </div>
  </Modal>
</template>

<script setup>
import { ref, getCurrentInstance, nextTick, onBeforeUnmount } from 'vue';
import Modal from '../ui/Modal.vue';
import Loading from '../ui/Loading.vue';
import { ElSelect, ElOption, ElInput, ElButton } from 'element-plus';
import log from '../../utils/Log';

const app = getCurrentInstance();
const ajax = app.appContext.config.globalProperties.$ajax;
const showModal = ref(false);
const selectRef = ref(null);
const users = ref([]);
const allTags = ref([]); // 保存所有 tags
const filteredUsers = ref([]); // 过滤后的用户列表
const isLoading = ref(true);
const isSearching = ref(false);
const isSending = ref(false);
const value = ref('');  
const message = ref('');
const isShowError1 = ref(false);
const isShowError2 = ref(false);
const currentKeyword = ref(''); // 当前搜索关键词

// 分页状态
const userPagination = ref({
  active: { current_page: 0, last_page: 1, loading: false },
  muted: { current_page: 0, last_page: 1, loading: false },
});
const loadingMore = ref(false);

// 获取单个状态的用户列表
const getUsersByStatus = (status, keyword = '', page = 1) => {
  return new Promise((resolve, reject) => {
    const keywordParam = keyword ? `&keyword=${encodeURIComponent(keyword)}` : '';
    ajax.get(`/chatroom/get-creator-list?status=${status}&page=${page}${keywordParam}`)
      .then((res) => {
        const newItems = res.data.data.data || [];
        
        userPagination.value[status].current_page = res.data.data.current_page || page;
        userPagination.value[status].last_page = res.data.data.last_page || 1;
        
        resolve(newItems);
      })
      .catch((error) => {
        console.error(`Error fetching ${status} creators:`, error);
        reject(error);
      });
  });
};

// 更新 filteredUsers 列表
const updateFilteredUsers = () => {
  const keyword = currentKeyword.value.toLowerCase();
  
  // 过滤 tags
  const matchedTags = keyword 
    ? allTags.value.filter((tag) => tag._originalTag.toLowerCase().includes(keyword))
    : allTags.value;
  
  // tags 在前面 + users
  filteredUsers.value = [...matchedTags, ...users.value];
};

// 加载更多用户
const loadMoreUsers = async () => {
  const activeP = userPagination.value.active;
  const mutedP = userPagination.value.muted;
  
  // 检查是否还有更多数据
  const hasMoreActive = activeP.current_page < activeP.last_page;
  const hasMoreMuted = mutedP.current_page < mutedP.last_page;
  
  if (!hasMoreActive && !hasMoreMuted) return;
  if (loadingMore.value) return;
  
  loadingMore.value = true;
  
  try {
    const promises = [];
    
    if (hasMoreActive) {
      promises.push(
        getUsersByStatus('active', currentKeyword.value, activeP.current_page + 1)
          .then(items => ({ status: 'active', items }))
      );
    }
    
    if (hasMoreMuted) {
      promises.push(
        getUsersByStatus('muted', currentKeyword.value, mutedP.current_page + 1)
          .then(items => ({ status: 'muted', items }))
      );
    }
    
    const results = await Promise.all(promises);
    
    results.forEach(({ items }) => {
      users.value = [...users.value, ...items];
    });
    
    updateFilteredUsers();
  } finally {
    loadingMore.value = false;
  }
};

// 处理下拉框滚动
const handleScroll = (e) => {
  const target = e.target;
  const scrollHeight = target.scrollHeight;
  const scrollTop = target.scrollTop;
  const clientHeight = target.clientHeight;
  
  // 滚动到底部时加载更多
  if (scrollHeight - scrollTop - clientHeight < 50) {
    loadMoreUsers();
  }
};

// 下拉框显示/隐藏时绑定/解绑滚动事件
const handleVisibleChange = (visible) => {
  nextTick(() => {
    const selectDom = selectRef.value?.$el;
    if (!selectDom) return;
    
    const scrollContainer = document.querySelector('.el-select-dropdown .el-select-dropdown__wrap');
    
    if (visible && scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
    } else if (scrollContainer) {
      scrollContainer.removeEventListener('scroll', handleScroll);
    }
  });
};

const getUsers = async () => {
  try {
    // 重置分页状态
    userPagination.value = {
      active: { current_page: 0, last_page: 1, loading: false },
      muted: { current_page: 0, last_page: 1, loading: false },
    };
    users.value = [];
    currentKeyword.value = '';
    
    // 并行请求第一页用户和所有 tags
    const [activeUsers, mutedUsers, tagsRes] = await Promise.all([
      getUsersByStatus('active', '', 1),
      getUsersByStatus('muted', '', 1),
      ajax.get('/creator/get-ambassador-tag-list'),
    ]);
    
    log('blue', 'Initial data loaded');
    
    users.value = [...activeUsers, ...mutedUsers];
    
    // 保存所有 tags
    allTags.value = tagsRes.data.data.map((item) => ({
      id: item.id,
      name: 'Tag:' + item.tag,
      is_tag: true,
      _originalTag: item.tag,
    }));
    
    updateFilteredUsers();
    isLoading.value = false;
  } catch (error) {
    console.error('Error loading users:', error);
    isLoading.value = false;
  }
};

const remoteSearch = async (keyword) => {
  currentKeyword.value = keyword || '';
  
  if (!keyword) {
    // 无关键词时显示所有数据
    updateFilteredUsers();
    return;
  }

  isSearching.value = true;
  
  // 重置分页
  userPagination.value = {
    active: { current_page: 0, last_page: 1, loading: false },
    muted: { current_page: 0, last_page: 1, loading: false },
  };
  users.value = [];

  try {
    // 搜索时请求第一页
    const [activeUsers, mutedUsers] = await Promise.all([
      getUsersByStatus('active', keyword, 1),
      getUsersByStatus('muted', keyword, 1),
    ]);
    
    users.value = [...activeUsers, ...mutedUsers];
    updateFilteredUsers();
  } catch (error) {
    console.error('Search error:', error);
  } finally {
    isSearching.value = false;
  }
};

const send = () => {
  if (value.value.length === 0 || message.value === '') {
    isShowError1.value = value.value.length === 0;
    isShowError2.value = message.value === '';
    return;
  }

  isShowError1.value = false;
  isShowError2.value = false;
  isSending.value = true;

  const data = {
    // creator_portal_user_ids: value.value,
    message_text: message.value,
  };
  const ambassador_program_tag_ids = [];
  const creator_portal_user_ids = [];

  value.value.forEach((item) => {
    // 先从 allTags 中查找是否是 tag
    const isTag = allTags.value.find((tag) => tag.id === item);
    if (isTag) {
      ambassador_program_tag_ids.push(item);
    } else {
      creator_portal_user_ids.push(item);
    }
  });

  if (ambassador_program_tag_ids.length > 0) {
    data.ambassador_program_tag_ids = ambassador_program_tag_ids;
  }

  if (creator_portal_user_ids.length > 0) {
    data.creator_portal_user_ids = creator_portal_user_ids;
  }

  log('blue', 'send ids', data);

  ajax.post('/chatroom/send-new-message', data).then((res) => {
    log('green', 'Send message', res);
    close();
  });
};

const open = () => {
  if (users.value.length === 0) {
    getUsers();
  } else {
    isLoading.value = false;
  }

  showModal.value = true;
};

const close = () => {
  console.log('close');

  showModal.value = false;
  message.value = '';
  value.value = '';
  isSending.value = false;
  isShowError1.value = false;
  isShowError2.value = false;
};

defineExpose({
  open,
  close,
});
</script>

<style lang="scss">
.new-chat-modal {
  z-index: 90;

  .box {
    padding: 24px;

    & > header {
      margin-bottom: 16px;

      h2 {
        font: var(--medium-24);
      }
    }

    .form-item {
      margin-bottom: 16px;

      label {
        display: block;
        margin-bottom: 8px;
        font: var(--medium-16);
      }

      p {
        margin-top: 8px;
        font: var(--book-14);
        color: var(--Text-default);
      }
    }

    .error-msg {
      margin-bottom: 4px;
      font: var(--book-12);
      color: var(--Red-30);
    }

    footer {
      display: flex;
      margin-top: 24px;

      .el-button + .el-button {
        flex: 1;
        align-items: center;
        display: flex;
        justify-content: center;
      }
    }
  }
}

.select-loading-more {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0 32px;
  color: var(--black-50);
  font-size: 12px;

  .circular {
    width: 20px;
    height: 20px;
    margin-right: 8px;
    animation: loading-rotate 2s linear infinite;
  }

  .path {
    stroke: var(--dark-blue);
    stroke-width: 3;
    stroke-linecap: round;
    animation: loading-dash 1.5s ease-in-out infinite;
  }
}

@keyframes loading-rotate {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes loading-dash {
  0% {
    stroke-dasharray: 1, 200;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -40px;
  }
  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -120px;
  }
}
</style>
